import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Typography,
  Icon,
  Checkbox,
  Search,
} from '@safedns/skydns-ui-react-lib';
import CheckboxTree from 'react-checkbox-tree';
import 'react-checkbox-tree/lib/react-checkbox-tree.css';

import sprite from '../../icons/sprite.svg';

import { reducedUsersToTheirInitialSize } from '../../store/usersReducer';
import {
  treeUpdated,
  updateSelectedGroup,
  undoSelectedGroup,
  resetSelectedGroups,
  parseUserIdsForBackend,
  undoUsersOfSelectedGroup,
  selectAllUsersOfSelectedGroup,
  updateSelectedGroups,
  undoUsers,
  createArrayFromSelectedGroups,
} from '../../store/treeReducer';

import './Collections.scss';

import { fetchAllUsersOfSelectedGroup } from '../../asyncActions/fetchAllUsersOfSelectedGroup';
import {
  useGetUserQuery,
  useGetDomainsQuery,
  useGetGroupsQuery,
  useGetUsersWithProfilesQuery,
} from '../../services/ADapi';

import {
  createUsersMap,
  createGroupTree,
  createTree,
} from '../../utils/makeTree';

const treeIcons = {
  check: <Icon id="check" sprite={sprite} width={20} height={20} />,
  halfCheck: <Icon id="halfcheck" sprite={sprite} width={20} height={20} />,
  uncheck: <Icon id="incheck" sprite={sprite} width={20} height={20} />,
  expandClose: <Icon id="close" sprite={sprite} width={14} height={14} />,
  expandOpen: <Icon id="expand" sprite={sprite} width={14} height={14} />,
};

const CollectionsConfigurate = ({ checkedUsers }) => {
  const dispatch = useDispatch();
  const filteredUsersFromApi = useSelector(
    (state) => state.users.filteredUsersFromApi
  );
  const tree = useSelector((state) => state.tree.tree);
  const selectedGroups = useSelector((state) => state.tree.selectedGroups);
  const arrayOfSelectedItems = useSelector(
    (state) => state.tree.arrayOfSelectedItems
  );
  const {
    data: { user_id },
  } = useGetUserQuery();
  const { data: DOMAINS } = useGetDomainsQuery(user_id);
  const { data: GROUPS, isLoading: isGroupsLoading } =
    useGetGroupsQuery(user_id);
  const { data: USERS, isLoading: areUsersWithProfilesLoading } =
    useGetUsersWithProfilesQuery(user_id);

  const [expanded, setExpanded] = useState([]);
  const [filterText, setFilterText] = useState('');
  const [nodesFiltered, setNodesFiltered] = useState(tree);
  const [lastChildren, setLastChildren] = useState([]);
  const [breadcrumbs, setIsBreadcrumbs] = useState([]);
  // const [pageCount, setPage] = useState(1);
  const [rawGroupId, setRawGroupId] = useState(0);
  const [groupFilterText, setGroupFilterText] = useState('');
  const [selectAllControlState, setSelectAllControlState] = useState(false);

  // This will roll back users array to its initial size
  // otherewise there'll be an error with duplicating ids
  useEffect(() => {
    dispatch(reducedUsersToTheirInitialSize());

    // Reset selected users when leave this component
    return () => {
      dispatch(resetSelectedGroups());
    };
  }, [dispatch]);

  useEffect(() => {
    const users =
      filteredUsersFromApi.length > 0 ? filteredUsersFromApi : USERS;

    const usersMap = createUsersMap(users || []);
    const groupsTree = createGroupTree(GROUPS || [], null, usersMap);
    const tree = createTree(DOMAINS || [], groupsTree);

    dispatch(treeUpdated(tree));
  }, [USERS, GROUPS, DOMAINS, filteredUsersFromApi, dispatch]);

  // This effect renders previously selected users in created collections
  useEffect(() => {
    if (checkedUsers && !isGroupsLoading) {
      GROUPS.forEach((group) => {
        // checking for a match of selected users in the tree
        const filterSelected = group.ad_users.filter((user) =>
          checkedUsers.includes(user)
        );

        filterSelected.forEach((userId) => {
          const key = `d${group.domain}-g${group.id}-u${userId}`;

          dispatch(updateSelectedGroups(key));
        });
      });
    }
  }, [GROUPS, checkedUsers, tree, isGroupsLoading, dispatch]);

  useEffect(() => {
    setNodesFiltered(tree);
  }, [tree, dispatch]);

  useEffect(() => {
    dispatch(createArrayFromSelectedGroups());
    dispatch(parseUserIdsForBackend());
  }, [selectedGroups, dispatch]);

  useEffect(() => {
    setSelectAllControlState(false);

    if (rawGroupId && selectedGroups[rawGroupId]) {
      const groupSize = Object.values(selectedGroups[rawGroupId]).length;

      if (groupSize >= lastChildren.length) {
        setSelectAllControlState(true);
      } else {
        setSelectAllControlState(false);
      }
    }
  }, [rawGroupId, selectedGroups, lastChildren, setSelectAllControlState]);

  // Tree filtering section

  const filterNodes = (filtered, node) => {
    const children = (node.children || []).reduce(filterNodes, []);

    const q = filterText.toLowerCase();

    const matchesQuery =
      node.label && node.label.toLowerCase().includes(q.toLowerCase());
    const hasNoUserFields =
      node.token === undefined &&
      node.hostname === undefined &&
      node.last_seen === undefined;

    if (hasNoUserFields && (matchesQuery || children.length)) {
      filtered.push({ ...node, ...(children.length && { children }) });
    }

    return filtered;
  };

  const filterTree = () => {
    if (!filterText) {
      setNodesFiltered(tree);

      return;
    }

    const filteredTree = tree.reduce(filterNodes, []);

    setExpanded((prevState) => {
      if (filteredTree.length && prevState) {
        return [...prevState, filteredTree[0].value];
      }
    });

    setNodesFiltered(filteredTree);
  };

  const onFilterHandler = (e) => {
    setFilterText(e.target.value);

    e.target.value ? filterTree() : setNodesFiltered(tree);
  };

  const onGroupFilterHandler = (e) => {
    setGroupFilterText(e.target.value);
  };

  const onCheckHandler = (event, itemId) => {
    const checked = event.target.checked;

    if (itemId.includes('groupId')) {
      if (checked) {
        dispatch(updateSelectedGroup(itemId));
      } else {
        dispatch(undoSelectedGroup(itemId));
      }

      return;
    }

    if (checked) {
      dispatch(updateSelectedGroups(itemId));
    } else {
      dispatch(undoUsers(itemId));
    }
  };

  const selectAllHandler = (e) => {
    const isChecked = e.target.checked;

    setSelectAllControlState(isChecked);

    if (isChecked) {
      if (lastChildren.length > 40) {
        dispatch(fetchAllUsersOfSelectedGroup(user_id, rawGroupId));
      } else {
        dispatch(selectAllUsersOfSelectedGroup(rawGroupId));
      }
    } else {
      dispatch(undoUsersOfSelectedGroup(rawGroupId));
    }
  };

  const breadcrumbsHandler = (treeDepth, label) => {
    const insertAt = treeDepth;
    const nextBreadcrumbs = [...breadcrumbs.slice(0, insertAt), label];

    setIsBreadcrumbs(nextBreadcrumbs);
  };

  let rightColumnUsers = lastChildren;

  if (groupFilterText.length > 0) {
    rightColumnUsers = lastChildren.filter((child) =>
      child.label.toLowerCase().includes(groupFilterText.toLowerCase())
    );
  }

  return (
    <>
      <div className="breadcrumbs">
        {breadcrumbs.map((crumb, index) => {
          const crumbsLength = breadcrumbs.length - 1;
          return (
            <div key={`${index}-crumb`}>
              <span>{crumb}</span>
              {index < crumbsLength ? (
                <span className="separator">/</span>
              ) : null}
            </div>
          );
        })}
      </div>
      <div className="columns">
        <div className="tree">
          <div className="tree-search">
            <Search
              value={filterText}
              placeholder="Поиск по пользователям"
              onChange={onFilterHandler}
            />
          </div>
          <CheckboxTree
            nodes={nodesFiltered}
            checked={arrayOfSelectedItems}
            expanded={expanded}
            disabled
            onExpand={setExpanded}
            showNodeIcon={false}
            expandOnClick
            onClick={(node) => {
              // Firstly reset group id. It's needed to separate same logic between groups
              // For example, Select all control relies on that, switching between groups should disable Select all control
              setRawGroupId(0);
              // Store a raw number of group id. Only a number 6 will be saved from {groupId: 'd1-g6'}
              const groupId = node.value.split('-groupId')[1];
              setRawGroupId(groupId);

              if (!node.isChild) {
                setIsBreadcrumbs([node.label]);
              } else {
                breadcrumbsHandler(node.treeDepth, node.label);
              }

              if (node.children) {
                setLastChildren(node.children);
              }
            }}
            icons={treeIcons}
          />
        </div>
        <div className="users">
          <div className="group-search">
            <Search
              value={groupFilterText}
              placeholder="Поиск по группе"
              onChange={onGroupFilterHandler}
            />
          </div>
          <Checkbox
            isDisabled={rawGroupId === 0 || rawGroupId === undefined}
            label="Выбрать все"
            sx={{
              marginTop: 24,
              padding: '8px 4px',
            }}
            isChecked={selectAllControlState}
            onChange={selectAllHandler}
          />

          {areUsersWithProfilesLoading ? (
            <div className="onscroll-loader">
              <Icon id="loader" sprite={sprite} width={30} height={30} />
              <Typography variant="caption">
                Пожалуйста, подождите...
              </Typography>
            </div>
          ) : null}

          {rightColumnUsers.length ? (
            <>
              {rightColumnUsers.map((child) => {
                const isUserNode = child.hasOwnProperty('last_seen');
                const icon = isUserNode ? (
                  <Icon id="user" sprite={sprite} width={16} height={16} />
                ) : (
                  <Icon id="group" sprite={sprite} width={16} height={16} />
                );

                let isGroupSelected = false;

                // Here make sure that current child is a group with children
                if (child.children && child.children.length > 0) {
                  // Run check to make sure that every item from group's children has 'checked' state
                  // which means that group should be checked
                  const groupId = child.value.split('-groupId')[1];

                  if (groupId && selectedGroups[groupId]) {
                    const groupSize = Object.values(
                      selectedGroups[groupId]
                    ).length;

                    // Since we show 50 users per page as default, actual group size can be more than 50 users
                    isGroupSelected = groupSize >= child.children.length;
                  }
                }

                return (
                  <Checkbox
                    key={child.value}
                    label={
                      <div style={{ display: 'flex', gap: 4 }}>
                        {icon}
                        {child.label}
                      </div>
                    }
                    isChecked={
                      isGroupSelected
                        ? isGroupSelected
                        : arrayOfSelectedItems.includes(child.value)
                    }
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      padding: '8px 4px',
                    }}
                    onChange={(event) => onCheckHandler(event, child.value)}
                  />
                );
              })}
            </>
          ) : null}
        </div>
      </div>
    </>
  );
};

export default CollectionsConfigurate;
